'use client'

import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

interface PremiumProgressProps {
  className?: string
  /** Premium students the school has now. */
  current: number
  /** Premium students the school needs to clear the gate. */
  target: number
  /** Label above the bar. Defaults to "Menuju {target} siswa Premium". */
  label?: string
}

/**
 * "Menuju N siswa Premium" with a bar and an `x/N` readout.
 *
 * Hand-rolled track + fill rather than a `Progress` component, because there is no such component
 * in `@ui/web` and every other bar in the app is built this way — see `MetricBar` in
 * `locked-badge-modal.tsx`, whose markup this mirrors.
 *
 * `role='progressbar'` with the aria value trio, so a screen reader gets the same "5 of 8" the
 * sighted readout gives rather than a bare decorative div.
 */
export function PremiumProgress({ className, current, target, label }: PremiumProgressProps) {
  // A school past the threshold keeps a full bar rather than an overflowing one.
  //
  // `target <= 0` reads as EMPTY, not full: the thresholds report 0 for a non-school organization,
  // and a 100%-filled bar labelled "0/0" on a screen that is visibly locked says the opposite of
  // what the screen means.
  const ratio = target > 0 ? Math.min(1, Math.max(0, current / target)) : 0

  return (
    <div className={cn('srd:flex srd:w-full srd:flex-col srd:gap-1', className)}>
      {/* `text-left` is explicit because both locks centre their card, and an inherited
          `text-center` would float this label away from the bar it labels. */}
      <span className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-neutral-black srd:text-left')}>
        {label ?? `Menuju ${target} murid Premium`}
      </span>

      <div className='srd:flex srd:items-center srd:gap-2'>
        <div
          role='progressbar'
          aria-valuenow={current}
          aria-valuemin={0}
          aria-valuemax={target}
          aria-label={label ?? `Menuju ${target} murid Premium`}
          className='srd:bg-grey-100/20 srd:h-4 srd:flex-1 srd:overflow-hidden srd:rounded-full'
        >
          <div
            className='srd:bg-blue-70 srd:h-full srd:rounded-full srd:transition-[width] srd:duration-500'
            style={{ width: `${ratio * 100}%` }}
          />
        </div>

        <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-black srd:shrink-0')}>
          {current}/{target}
        </span>
      </div>
    </div>
  )
}
